<!-- eslint-disable vue/no-use-v-if-with-v-for -->
<template>
  <div>
    <el-radio-group v-if="!isDesktop" v-model="viewType" class="padding-5">
      <el-radio-button key="Mobile" value="Mobile"> Mobile </el-radio-button>
      <el-radio-button key="Full" value="Full"> Full view </el-radio-button>
    </el-radio-group>
    <el-card :class="{ mobile: viewType === 'Mobile' && !isDesktop }">
      <ExportButton v-show="items.length > 1" @click="download" />
      <el-button
        v-show="items.length > 1 && (viewType === 'Full' || isDesktop)"
        class="ml-1"
        size="small"
        @click="fullscreen = !fullscreen"
      >
        Fullscreen
      </el-button>
      <fullscreen v-model="fullscreen">
        <el-button
          v-show="fullscreen"
          type="danger"
          @click="fullscreen = !fullscreen"
        >
          Exit
        </el-button>
        <el-table
          v-show="isDesktop || viewType === 'Full'"
          :data="items"
          border
          show-summary
          size="small"
          class="reports-table"
          :summary-method="getSummaries"
          :header-cell-class-name="headerCellClassName"
          style="width: 100%; margin-top: 20px; font-size: 12px"
        >
          <template v-if="groups.length">
            <el-table-column
              :width="colWidth('name')"
              prop="name"
              fixed
              sortable
            >
              <template #header>
                <span> Group by </span>
              </template>
              <template #default="scope">
                <span :class="colClass('name', formatter(scope.row, 'name'))">{{
                  formatter(scope.row, 'name')
                }}</span>
              </template>
            </el-table-column>
            <el-table-column
              v-for="item in groups"
              :key="item.title"
              :label="item.title"
            >
              <!-- eslint-disable-next-line -->
              <template v-for="(col, index) in tableData.tableColumns" :key="index">
                <template v-if="colIsVisible(item, col)">
                  <el-table-column
                    :key="`${index}_col`"
                    :prop="col"
                    :width="colWidth(col)"
                    sortable
                  >
                    <template #header>
                      <span
                        v-if="'change' === tableData.tableColumnsHeaders[index]"
                      >
                        Δ
                        <IconWithHint>
                          {{
                            tableData.hints[
                              tableData.tableColumnsHeaders[index]
                            ]
                          }}
                        </IconWithHint>
                      </span>
                      <span v-else>
                        {{
                          tableData.headers[
                            tableData.tableColumnsHeaders[index]
                          ]
                        }}
                        <IconWithHint>
                          {{
                            tableData.hints[
                              tableData.tableColumnsHeaders[index]
                            ]
                          }}
                        </IconWithHint>
                      </span>
                    </template>
                    <template #default="scope">
                      <span :class="colClass(col, formatter(scope.row, col))">{{
                        formatter(scope.row, col)
                      }}</span>
                    </template>
                  </el-table-column>
                </template>
              </template>
            </el-table-column>
          </template>
          <template v-else>
            <el-table-column
              v-for="(col, index) in tableData.tableColumns"
              :key="index"
              :prop="col"
              :width="colWidth(col)"
              sortable
            >
              <template #header>
                <span v-if="'change' === tableData.tableColumnsHeaders[index]">
                  Δ
                  <IconWithHint>
                    {{ tableData.hints[tableData.tableColumnsHeaders[index]] }}
                  </IconWithHint>
                </span>
                <span v-else>
                  {{ tableData.headers[tableData.tableColumnsHeaders[index]] }}
                  <IconWithHint>
                    {{ tableData.hints[tableData.tableColumnsHeaders[index]] }}
                  </IconWithHint>
                </span>
              </template>
              <template #default="scope">
                <span :class="colClass(col, formatter(scope.row, col))">{{
                  formatter(scope.row, col)
                }}</span>
              </template>
            </el-table-column>
          </template>
        </el-table>
      </fullscreen>
      <el-table
        v-show="viewType === 'Mobile' && !isDesktop"
        :data="items"
        style="width: 100%"
        class="reports-table"
      >
        <el-table-column type="expand">
          <template #default="props">
            <p v-for="(col, index) in tableData.tableColumns" :key="index">
              <b>{{
                tableData.headers[tableData.tableColumnsHeaders[index]]
              }}</b
              >: {{ props.row[col] }}
            </p>
          </template>
        </el-table-column>
        <el-table-column label="Name" prop="name" />
      </el-table>
    </el-card>
  </div>
</template>

<script lang="ts">
import useResize from '@/hooks/useResize'

import { defineComponent, computed, ref, watch } from 'vue'
import IconWithHint from '@/components/IconWithHint.vue'
import ExportButton from '@/components/ExportButton.vue'

const HEADERS: Record<string, string> = {}
const DEFAULT_PRECISION: number = 2
const PERCENT_COLUMNS: string[] = ['ectr']

const PRECISION: Record<string, number> = {
  impressions: 0,
  clicks: 0,
  vast_click: 0,
  requests: 0,
  conversions: 0,
  bots: 0,
  proxies: 0,
  broker_hits: 0,
  broker_clicks: 0,
  referer_mismatch_conversion: 0,
  ua_mismatch_conversion: 0,
  ip_mismatch_conversion: 0,
  interstitial_open: 0,
  open_rate: 0,
  banner_view: 0,
  fill_rate: 0,
  banner_view_rate: 2,
  predicted_income: 2,
  ecpa: 3,
  ectr: 3,
  real_cpm: 3,
  predicted_cpm: 3,
  cpc: 3,
  banner_ctr: 3,
  real_cpm_open: 3,
  broker_cpm: 3,
  broker_cpc: 3
}

const COL_WIDTH: Record<string, number> = {
  creature: 500,
  website: 200,
  spot: 300,
  referer_host: 200,
  campaign: 200
}

export default defineComponent({
  name: 'ReportTable',
  components: {
    IconWithHint,
    ExportButton
  },
  props: {
    tableData: {
      type: Object,
      required: true
    },
    group: {
      type: String,
      required: true
    },
    skipZero: {
      type: Boolean,
      default: false
    },
    groups: {
      type: Array,
      default: () => []
    }
  },
  setup(props, context) {
    const viewType = ref('Mobile')
    const fullscreen = ref(false)
    const { isDesktop } = useResize()
    const colIsVisible = (group: any, col: string) => {
      return group.items.includes(col)
    }
    const getSummaries = (param: any) => {
      const { columns, data } = param
      const totals: Record<number, any> = {}
      columns.forEach((col: any, index: number) => {
        if (index === 0) {
          totals[index] = 'Total'
        } else {
          let value = props.tableData.total[col.property]
          const formatedCol = formatColName(col.property)
          if (PERCENT_COLUMNS.includes(formatedCol)) {
            value = value * 100
          }
          totals[index] = value
            ? value
                .toFixed(precision(formatedCol))
                .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            : '0'
        }
      })
      return totals
    }

    const colWidth = (col: string) => {
      return col === 'name'
        ? COL_WIDTH[props.group] || 'auto'
        : col.match(/_change/)
          ? 60
          : 'auto'
    }

    const colClass = (col: string, value: any) => {
      if (col.match(/_change/)) {
        return value > 0 ? 'plus' : value < 0 ? 'minus' : 'default'
      }
      return 'default'
    }

    const items = computed(() => {
      return props.tableData.data.filter((x: any) => {
        if (
          props.group === 'adnetwork_squashed' &&
          props.tableData.data.find(
            (item: any) => item.name === 'Other Networks'
          )
        ) {
          const otherNetworksItem = props.tableData.data.find(
            (item: any) => item.name === 'Other Networks'
          )
          otherNetworksItem.name = 'Own deals'
        }
        if (props.group === 'referer_host') {
          return x.clicks > 0 || x.vast_click > 0 || x.impressions > 0
        }
        if (props.skipZero) {
          return x.hits > 0
        }
        return x.hits > 0 || x.broker_hits > 0 || x.impressions > 0
      })
    })

    const precision = (col: string) => {
      if (PRECISION[col] !== undefined) {
        return PRECISION[col]
      }
      return DEFAULT_PRECISION
    }

    const formatColName = (col: string) => {
      return String(col).replace(/[^a-zA-Z_]/g, '')
    }

    const formatter = (row: any, col: any) => {
      let value = row[String(col)]
      const formatedCol = formatColName(String(col))
      if (PERCENT_COLUMNS.includes(formatedCol)) {
        value = `${(value * 100).toFixed(precision(formatedCol))}%`
      } else if (typeof value === 'number') {
        value = value
          .toFixed(precision(formatedCol))
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      }

      return value
    }

    const download = (format: string) => {
      context.emit('download', format)
    }
    const headerCellClassName = (row: any) => {
      return row.column?.property ? 'header-cell' : 'group-cell'
    }

    return {
      colIsVisible,
      isDesktop,
      getSummaries,
      formatter,
      colWidth,
      colClass,
      download,
      items,
      viewType,
      fullscreen,
      headerCellClassName
    }
  }
})
</script>
<style lang="scss" scoped>
.el-card {
  padding: 0;
  margin-top: 10px;
  position: relative;
}

:deep(.el-table) {
  :deep(.caret-wrapper) {
    width: 10px;
  }

  :deep(.group-cell) {
    text-align: center;

    :deep(.cell) {
      font-size: 14px;
    }
  }

  :deep(th > .cell) {
    padding: 0 0 0 2px;
    font-size: 8px;

    :deep(span) {
      word-break: break-word;
    }
  }

  :deep(.minus) {
    color: red;
  }

  :deep(.plus) {
    color: green;
  }

  :deep(tbody .cell) {
    font-size: 14px;
  }
}

.el-link {
  right: 20px;
  position: absolute;
  top: 10px;
  z-index: 2;
}
</style>
